import { LocaleImage } from '@/i18n/router';
import { useMessages } from '@/i18n/LocaleProvider';
import messages from '@/i18n/catalogs/corporate/GizmoEdgeSection';
import React from 'react';

import { Link } from '@/i18n/router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import SectionHeading from '@/components/SectionHeading';
import { ArrowRight, Server, Globe, Cpu, Smartphone } from 'lucide-react';
import gizmoEdgeIcon from '@/assets/gizmoedge-logo-icon.png';
import AnimatedSection from './AnimatedSection';

// GizmoEdge product section — split out of ProductsSection so the homepage
// can order GizmoSQL → GizmoData Cloud → Cloud at Customer → GizmoEdge.
const GizmoEdgeSection = () => {
  const m = useMessages(messages);


  return (
    <section id="gizmoedge-section" className="scroll-mt-24 relative">
      {/* GizmoEdge Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900 text-white overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-gizmo-400/15 rounded-full blur-3xl" />
          {/* Grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slide-left" className="lg:order-2">
              <div className="space-y-6">
                <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 hover:bg-purple-500/30 transition-colors">{m.s1}</Badge>
                <div className="flex items-center gap-4">
                  <LocaleImage
                    src={gizmoEdgeIcon}
                    alt={m.s2}
                    width={530}
                    height={490}
                    className="h-16 w-16 object-contain"
                  />
                  <SectionHeading withId anchor="gizmoedge" as="h2" className="text-4xl md:text-5xl font-bold">GizmoEdge</SectionHeading>
                </div>
                <p className="text-xl text-gray-300 leading-relaxed">{m.s3}</p>

                {/* Feature highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                  <div className="flex items-start gap-3 group">
                    <div className="p-2 bg-white/5 rounded-lg group-hover:bg-white/10 transition-colors">
                      <Globe className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{m.s4}</h3>
                      <p className="text-sm text-gray-400">{m.s5}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 group">
                    <div className="p-2 bg-white/5 rounded-lg group-hover:bg-white/10 transition-colors">
                      <Server className="h-5 w-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{m.s6}</h3>
                      <p className="text-sm text-gray-400">{m.s7}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 group">
                    <div className="p-2 bg-white/5 rounded-lg group-hover:bg-white/10 transition-colors">
                      <Smartphone className="h-5 w-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{m.s8}</h3>
                      <p className="text-sm text-gray-400">{m.s9}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 group">
                    <div className="p-2 bg-white/5 rounded-lg group-hover:bg-white/10 transition-colors">
                      <Cpu className="h-5 w-5 text-orange-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{m.s10}</h3>
                      <p className="text-sm text-gray-400">{m.s11}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 pt-4">
                  <Link to="/gizmoedge">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                      data-ga-cta="explore_gizmoedge"
                      data-ga-location="home_products"
                    >{m.s12}<ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="slide-right" className="lg:order-1">
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-2xl blur-2xl transform scale-105" />

                {/* Architecture visualization */}
                <div className="relative bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl shadow-2xl overflow-hidden p-8">
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold text-white/80">{m.s13}</h3>
                  </div>

                  {/* Coordinator */}
                  <div className="flex justify-center mb-6">
                    <div className="bg-gradient-to-r from-purple-500/30 to-blue-500/30 border border-purple-400/30 rounded-xl p-4 text-center">
                      <Server className="h-8 w-8 mx-auto mb-2 text-purple-400" />
                      <span className="text-sm font-medium">{m.s14}</span>
                    </div>
                  </div>

                  {/* Connection lines */}
                  <div className="flex justify-center mb-4">
                    <div className="w-px h-8 bg-gradient-to-b from-purple-400/50 to-blue-400/50" />
                  </div>

                  {/* Workers */}
                  <div className="grid grid-cols-4 gap-3">
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className="bg-white/5 border border-white/10 rounded-lg p-3 text-center hover:bg-white/10 transition-colors group"
                        style={{ animationDelay: `${i * 100}ms` }}
                      >
                        <Cpu className="h-5 w-5 mx-auto mb-1 text-blue-400 group-hover:text-blue-300 transition-colors" />
                        <span className="text-xs text-gray-400">{m.s15}{i + 1}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/10 text-center">
                    <p className="text-white/60 text-sm">{m.s16}</p>
                    <p className="text-2xl font-bold text-white mt-1">{m.s17}<span className="text-sm font-normal text-green-400">{m.s18}</span>
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GizmoEdgeSection;
